import { InjectionToken, inject } from '@angular/core';

import type { Capability } from '../model/capabilities';
import { SessionService } from '../session/session.service';

/**
 * The swappable source of a user's effective capabilities for one partner area.
 *
 * The default {@link SessionCapabilitiesSource} reads the caps the backend has
 * already resolved into the readable `roles_token` (see `SessionService`). When
 * the fresh `GET /v1/me/capabilities` endpoint lands (backend `p1-12`), an
 * `HttpCapabilitiesSource` can be provided against {@link CAPABILITY_SOURCE}
 * instead — without touching `CapabilitiesService`, the guards, or the
 * `*hasCapability` directive.
 *
 * **UI gating only** — the backend `CapabilitiesGuard` is the real enforcement.
 */
export interface CapabilitySource {
  /**
   * The effective capabilities for `partnerId`. Called from inside a reactive
   * context (the `CapabilitiesService.capabilities` computed), so an
   * implementation that reads signals stays reactive automatically.
   */
  resolve(partnerId: string): readonly Capability[];

  /**
   * Re-fetch/refresh the underlying source, if it has one. A no-op for the
   * token-backed default (its data refreshes when the session re-reads the
   * cookie); an HTTP source would re-request here.
   */
  refresh?(): void;
}

/**
 * The default {@link CapabilitySource}: a per-partner lookup into the caps the
 * backend already resolved (role ∪ granted − revoked, active-seat-gated, then
 * closed under `CAPABILITY_IMPLIES`) and embedded in the `roles_token`. Reads the
 * `partnerAccess` signal, so callers inside a reactive context recompute when the
 * session changes.
 *
 * Because the closure runs last on the backend, a capability the member has
 * revoked can still appear here when something that implies it survived — see
 * `MembersService.computeEffective`. That is the resolved truth, not a stale
 * token.
 */
export class SessionCapabilitiesSource implements CapabilitySource {
  constructor(private readonly session: SessionService) {}

  resolve(partnerId: string): readonly Capability[] {
    return this.session.partnerAccess().find((p) => p.partnerId === partnerId)?.caps ?? [];
  }
}

/**
 * Injection token for the active {@link CapabilitySource}. Defaults to
 * {@link SessionCapabilitiesSource} so the library resolves caps from the token
 * with no HTTP dependency; override it to swap in an endpoint-backed source.
 */
export const CAPABILITY_SOURCE = new InjectionToken<CapabilitySource>('CAPABILITY_SOURCE', {
  providedIn: 'root',
  factory: () => new SessionCapabilitiesSource(inject(SessionService)),
});
